import { navigate } from 'gatsby';
import React, { FC, ReactNode, useContext, useState } from 'react';
import { animated, useSpring } from 'react-spring';
import styled, { ThemeContext } from 'styled-components';

import FolxLogo from '@ui/atoms/FolxLogo';
import TextButton, { IButtonProps } from '@ui/atoms/TextButton';

import DropdownNav from '@ui/molecules/DropdownNav';
import ProfileButton from '@ui/molecules/ProfileButton';

import fullWidth from '@ui/styled/FullWidth';

import { isBrowser } from '@utils/isBrowser';
import { getColorFromTheme } from '@utils/theme';

import { useAuth } from '@providers/AuthProvider';
import FlowContext from '@providers/FlowContextProvider';

import theme from '@theme/FolxTheme';

interface INewFlowHeaderProps {
	flowName: string;
	className?: string;
	shouldAnimate?: boolean;
	step?: number;
	totalSteps?: number;
	navItems?: ReactNode;
	showSignIn?: boolean;
}

const Header = styled.header`
	display: flex;
	flex-direction: column;
	width: 100%;
	position: sticky;
	top: 0;
	z-index: ${theme.zIndexes.header};
	background: ${({ theme: components }) =>
		getColorFromTheme(components.folxHeader.backgroundColor, components.folxHeader.backgroundTint)};
`;

const HeaderContent = styled.div`
	align-items: center;
	display: flex;
	height: ${theme.spacing.xxl};
	justify-content: space-between;
	padding: 0 ${theme.spacing.sm};

	@media ${theme.responsive.breakpoints.tablet} {
		padding: 0 ${theme.spacing.xxl};
	}
`;

const HeaderBar = styled(animated.div)`
	height: ${theme.spacing.sm};
`;

const StyledDropdownNav = styled(DropdownNav)`
	background: ${({ theme: components }) =>
		getColorFromTheme(components.folxHeader.backgroundColor, components.folxHeader.backgroundTint)};

	box-shadow: ${theme.shadow.box};
	position: absolute;
	top: calc(${theme.spacing.xxl} + ${theme.spacing.sm});
	right: 0;
`;

const FullWidthTextButton = fullWidth<IButtonProps>(TextButton);

const NewFlowHeader: FC<INewFlowHeaderProps> = ({
	className,
	flowName,
	navItems = <></>,
	shouldAnimate = true,
	showSignIn = true,
	step = 0,
	totalSteps = 1,
}) => {
	const [navOpen, setNavOpen] = useState(false);
	const { loggedIn } = useAuth();

	/** Theme variables */

	const components = useContext(ThemeContext);
	const { flow } = useContext(FlowContext);
	const activeColor = getColorFromTheme(
		components.headerBar.activeColor,
		components.headerBar.activeTint
	);
	const inactiveColor = getColorFromTheme(
		components.headerBar.inactiveColor,
		components.headerBar.inactiveTint
	);

	/** Header Bar */

	const stop = shouldAnimate ? (step / totalSteps) * 100 : 100;
	const backgroundAnimation = useSpring({
		background: `linear-gradient(to right,
				${activeColor} ${stop}%,
				${inactiveColor} ${stop}%
			)`,
	});

	return isBrowser ? (
		<Header className={className}>
			<HeaderContent>
				<FolxLogo />
				{loggedIn ? (
					<ProfileButton handleClick={() => setNavOpen(!navOpen)} open={navOpen} />
				) : (
					showSignIn && (
						<TextButton
							label="Sign In"
							onClick={() => {
								navigate('/app/sign_in', {
									state: {
										redirectTo: location?.pathname ?? '/',
										step,
										totalSteps,
										flow: flow != '' ? flow : undefined,
										previousPage: location?.pathname,
									},
									replace: true,
								});
							}}
						/>
					)
				)}
			</HeaderContent>
			<HeaderBar
				role="progressbar"
				aria-valuemin={0}
				aria-valuemax={totalSteps}
				aria-valuenow={step}
				aria-valuetext={`Step ${step} of ${totalSteps}`}
				aria-label={`${flowName} Flow Steps`}
				style={backgroundAnimation}
			/>
			<StyledDropdownNav label="Help and Sign Out" open={navOpen}>
				{navItems}
				<li role="presentation">
					<FullWidthTextButton
						role="menuitem"
						size="lg"
						label="Help"
						onClick={() => window.open(process.env.GATSBY_SUPPORT_LINK!, '__blank')}
					/>
				</li>
				<li role="presentation">
					<FullWidthTextButton
						role="menuitem"
						size="lg"
						label="Sign out"
						onClick={() => navigate('/logout')}
					/>
				</li>
			</StyledDropdownNav>
		</Header>
	) : (
		<div />
	);
};

export default styled(NewFlowHeader)``;
